import React, { useState, useEffect, useRef, forwardRef } from 'react'
import PropTypes from 'prop-types'
import DOMPurify from 'dompurify'
import {
  verticalAlignments,
  textAlignments,
  headingColors,
} from '../statistics/statsConstants.js'

import './card.css'
import { joinClassList } from '../utilities/utilities.js'
import { isProduction } from '../../utility/index.js'
import { camelCase } from 'lodash'
import { ErrorBoundary } from 'react-error-boundary'

import {
  isMobile,
  isScreenSmall,
  isScreenMedium,
  isScreenBig,
} from '../../utility/index.js'

const parseHeadingText = (text) => {
  if (!text) return { visibleText: text, accessibleLabel: null }
  const match = text.match(/^(.*?)\{(.+)\}\s*$/)
  if (match) {
    return { visibleText: match[1].trim(), accessibleLabel: match[2].trim() }
  }
  return { visibleText: text, accessibleLabel: null }
}

const Card = forwardRef(function Card({ minHeightRef, minHeight, ...props }) {
  const horizontalTextAlignment = textAlignments[props.cardTextAlignment]
  const headingColor = headingColors[props.headingColor] || 'u-textColorBrand'
  const themeReversed = props.theme === 'reversed'
  const prefixVerticalAlignment =
    verticalAlignments[props.prefixAlignment] || ''
  const suffixVerticalAlignment =
    verticalAlignments[props.suffixAlignment] || ''
  const getAlignment = (alignment, content) => {
    switch (alignment) {
      case 'top':
        return <sup className="u-textSizeMsrp4">{content}</sup>
      case 'bottom':
        return <span className="u-textSizeMsrp4">{content}</span>
      case 'middle':
        return <span className="u-textSizeMsrp5 u-alignMiddle">{content}</span>
      default:
        return <span className="u-textSizeMsrp4">{content}</span>
    }
  }

  const headingClasses = joinClassList([
    'Statistics-Heading--bold',
    'u-textSizeMsrp6',
    'Type-1',
    headingColor,
  ])

  const {
    visibleText: headingVisibleText,
    accessibleLabel: headingAccessibleLabel,
  } = parseHeadingText(props.headingText)

  const getColumnClass = (cardColumns) => {
    const columns = {
      whole: 'u-sm-sizeFull u-md-sizeFull u-size1of1',
      halves: 'u-sm-sizeFull u-md-sizeFull u-size1of2',
      thirds: 'u-sm-sizeFull u-md-sizeFull u-size1of3',
      fourths: 'u-sm-sizeFull u-md-sizeFull u-size1of4',
    }
    return columns[camelCase(cardColumns).trim()] || ''
  }

  const getOffset = (offset) => {
    const gridOffset = {
      none: '',
      '1of4': 'u-lg-before1of4 u-sm-sizeFull u-md-sizeFull',
      '2of4': 'u-lg-before2of4  u-sm-sizeFull u-md-sizeFull',
      '3of4': 'u-lg-before3of4 u-sm-sizeFull u-md-sizeFull',
    }
    return gridOffset[offset] || ''
  }

  return (
    <>
      {props.layout == 'horizontal' ? (
        <div className={getColumnClass(props.cardColumns)}>
          <div
            className={`CardColumn u-md-paddingVert8gu u-sm-paddingVert8gu ${horizontalTextAlignment}`}
          >
            {props.headingText && (
              <p className={headingClasses}>
                <span aria-hidden="true">
                  {props.prefix &&
                    getAlignment(prefixVerticalAlignment, props.prefix)}
                  <span
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(headingVisibleText),
                    }}
                  />
                  {props.suffix &&
                    getAlignment(suffixVerticalAlignment, props.suffix)}
                </span>
                {headingAccessibleLabel && (
                  <span className="u-hiddenVisually">
                    {headingAccessibleLabel}
                  </span>
                )}
              </p>
            )}
            <div>
              {props.subheadingText && (
                <p
                  className={`u-paddingBottom1gu  ${
                    themeReversed ? 'u-textColorWhite' : 'u-textColorBrand'
                  }`}
                  aria-hidden="true"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(props.subheadingText),
                  }}
                ></p>
              )}
              {props.bodyText && (
                <div
                  className={`body ${themeReversed ? 'u-textColorWhite' : ''}`}
                  aria-hidden="true"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(props.bodyText),
                  }}
                  ref={minHeightRef}
                  style={{ minHeight: minHeight }}
                ></div>
              )}
            </div>
            {props.link?.map((link) => {
              const text = link.text
              const href = link.link[0]?.linkHref
              const target = link.target
              return text && href ? (
                <a
                  className={`Link u-block ${horizontalTextAlignment} ${
                    themeReversed ? 'u-textColorWhite' : ''
                  }`}
                  href={href}
                  target={target}
                >
                  {text}
                </a>
              ) : null
            })}
          </div>
        </div>
      ) : (
        <div
          className={`u-flex u-sm-flexCol u-md-flexCol CardColumn u-md-paddingVert8gu u-paddingBottom3gu
              ${props.isInline ? '' : 'u-flexCol'}
              ${getColumnClass(props.cardColumns, props.layout)}
              ${getOffset(props.columnOffset)}
              ${horizontalTextAlignment}
              ${props.bodyText == '' ? `u-flexAlignItemsBaseline` : ''}
              `}
        >
          {props.headingText && (
            <p
              className={`u-marginRight3gu u-sm-marginRight0 u-md-marginRight0 
              ${props.isInline ? `u-lg-textRight` : ``}
              ${props.cardColumns == 'whole' ? `u-size1of4` : `u-size2of4`}
              ${headingClasses}`}
            >
              <span aria-hidden="true">
                {props.prefix &&
                  getAlignment(prefixVerticalAlignment, props.prefix)}
                <span
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(headingVisibleText),
                  }}
                />
                {props.suffix &&
                  getAlignment(suffixVerticalAlignment, props.suffix)}
              </span>
              {headingAccessibleLabel && (
                <span className="u-hiddenVisually">
                  {headingAccessibleLabel}
                </span>
              )}
            </p>
          )}

          {/* TODO: switch border to css prop so last child can be eliminated */}
          <div
            className={`BorderStyles u-sizeFull u-borderBottom
              ${
                props.theme == 'reversed'
                  ? 'u-borderColorReversed'
                  : 'u-borderColorNeutralLight'
              }
              `}
          >
            {props.subheadingText && (
              <p
                className={`u-paddingBottom1gu  ${
                  themeReversed ? 'u-textColorWhite' : 'u-textColorBrand'
                }`}
                aria-hidden="true"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(props.subheadingText),
                }}
              ></p>
            )}
            {props.bodyText && (
              <div className="">
                <div
                  className={`body ${themeReversed ? 'u-textColorWhite' : ''}`}
                  aria-hidden="true"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(props.bodyText),
                  }}
                  ref={minHeightRef}
                ></div>
                {props.link?.map((link) => {
                  const text = link.text
                  const href = link.link[0]?.linkHref
                  const target = link.target
                  return text && href ? (
                    <a
                      className={`Link u-block u-marginTop2gu ${horizontalTextAlignment} ${
                        themeReversed ? 'u-textColorWhite' : ''
                      }`}
                      href={href}
                      target={target}
                    >
                      {text}
                    </a>
                  ) : null
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  )
})

const getStatProps = (stat, parameters) => {
  return {
    prefix: stat.prefix,
    prefixAlignment: camelCase(stat.prefixAlignment),
    headingText: stat.stat,
    suffix: stat.suffix,
    suffixAlignment: camelCase(stat.suffixAlignment),
    headingColor: camelCase(stat.statColor).trim(),
    subheadingText: stat.subtitle?.value,
    bodyText: stat.html?.value,
    link: stat.link,
    cardColumns: camelCase(parameters.columns),
    layout: camelCase(parameters.layout),
    isInline: parameters.inlineContent,
    columnOffset: parameters.columnOffset,
    alignment: parameters.columnAlignment,
    cardTextAlignment: camelCase(
      parameters.cardtextAlignment || stat.cardTextAlignment
    ),
  }
}

const getStatKey = (stat) => {
  return `${stat.prefix}${stat.stat}${stat.subtitle}${stat.suffix}`.replaceAll(
    ' ',
    ''
  )
}

export const CardWrapper = ({ stats, parameters }) => {
  const [screenSize, setScreenSize] = useState(getScreenSize())
  const minHeightRef = useRef([])
  const [minHeight, setMinHieght] = useState(0)

  function getScreenSize() {
    const { name } = [
      isMobile,
      isScreenSmall,
      isScreenMedium,
      isScreenBig,
    ].find((el) => el())
    return name
  }
  useEffect(() => {
    //reset minHieght based on if the screen changes
    const handleResize = () => {
      const foundScreen = getScreenSize()
      if (foundScreen !== screenSize) {
        setScreenSize(foundScreen)
      }
    }
    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    minHeightRef.current = minHeightRef.current.slice(0, stats.length)

    const max = minHeightRef.current.reduce((maxHeight, currentRef) => {
      const rect = currentRef?.getBoundingClientRect()
      return Math.max(rect.height, maxHeight)
    }, 0)

    setMinHieght(max)
  }, [stats])

  const getGrid = (layout) => {
    const gridLayout = {
      vertical: '',
      horizontal: 'Grid Grid--withGutter',
    }
    return gridLayout[camelCase(layout).trim()] || ''
  }

  return (
    <ErrorBoundary
      fallbackRender={() =>
        !isProduction() ? <p>Error in Card component</p> : null
      }
    >
      <div
        className={`
        ${getGrid(parameters.layout)} 
        ${parameters.columnAlignment == 'Center' ? 'u-flexJustifyCenter' : ''}
        `}
      >
        {stats.map((stat, index) => {
          const props = {
            ...getStatProps(stat, parameters),
            theme: camelCase(parameters.theme),
          }
          const key = getStatKey(stat)
          return (
            <Card
              key={key}
              {...props}
              useImage={false}
              minHeight={screenSize !== 'isScreenBig' ? 'auto' : minHeight}
              minHeightRef={(el) => (minHeightRef.current[index] = el)}
            />
          )
        })}
      </div>
    </ErrorBoundary>
  )
}

export default Card

Card.propTypes = {
  headingText: PropTypes.string,
  headingColor: PropTypes.oneOf([
    'brandDark',
    'brandMedium',
    'teal',
    'raspberry',
    'yellow',
    'aqua',
    'white',
    '',
  ]),
  subheadingText: PropTypes.string,
  subscript: PropTypes.string,
  superscript: PropTypes.string,
  bodyText: PropTypes.string,
  linkText: PropTypes.string,
  linkUrl: PropTypes.string,
  cardTextAlignment: PropTypes.oneOf(['left', 'center', 'right', '']),
  theme: PropTypes.oneOf(['reversed', '']),
}
